import { localized } from "@msuite/ui/localization";
import { createColumnHelper, createRowActions } from "@msuite/ui/table";
import { moment } from "@msuite/utils/date";
import { getHoursString } from "@msuite/utils/numbers";
import { calculateBookings, getSettlementRecordState } from "@msuite/utils/partnerships";
import { guardString } from "@msuite/utils/strings";
import type { BookingsReviewListItem } from "@msuite/utils/types";
import { SettlementStateTag } from "@/components/partners";
import { BookingReviewRowActions } from "./booking-review-row-actions";

const columnHelper = createColumnHelper<BookingsReviewListItem>();

/**
 * The host's review queue, one row per settlement record. `onOpen` is the row's way into the
 * bookings modal, which the table's owner renders once for whichever row is selected.
 */
export function createColumns(onOpen: (booking: BookingsReviewListItem) => void) {
	return [
		columnHelper.accessor("date", {
			id: "date",
			header: localized("dates.date"),
			cell: (cell) => moment(cell.getValue()).dateFormat(),
		}),
		columnHelper.accessor((row) => `${row.organizationName} ${row.ranking}`, {
			id: "partner",
			header: localized("partners.partner"),
			cell: (cell) => cell.getValue(),
		}),
		columnHelper.accessor((row) => row.project.name, {
			id: "project",
			header: localized("projects.project"),
			cell: (cell) => (
				<div className="h-stack">
					<p>{cell.getValue()}</p>
					<p className="label">{guardString(cell.row.original.project.number)}</p>
				</div>
			),
		}),
		columnHelper.accessor(
			(row) => calculateBookings(row.ticketAssignmentReferenceBookings, null).submittedHours,
			{
				id: "submittedHours",
				header: localized("partners.submitted_hours"),
				cell: (cell) => getHoursString(cell.getValue()),
			},
		),
		columnHelper.accessor(
			(row) =>
				getSettlementRecordState({
					isApproved: row.isApproved,
					isReviewed: row.isReviewed,
					hasBookings: (row.ticketAssignmentReferenceBookings?.length ?? 0) > 0,
				}),
			{
				id: "state",
				header: localized("general.state"),
				cell: (cell) => <SettlementStateTag state={cell.getValue()} />,
			},
		),
		createRowActions<BookingsReviewListItem>((cell) => (
			<BookingReviewRowActions
				booking={cell.row.original}
				onOpen={onOpen}
			/>
		)),
	];
}
